import {Theme, ThemeMode, ThemePreference} from './types';
import {createTheme} from '@mui/material/styles';
import React, {createContext, ReactNode, useContext, useEffect, useState} from 'react';
import {ThemeProvider as EmotionThemeProvider} from '@emotion/react';
import {localStorageGetThemePreference, localStorageSetThemePreference} from './localStorage';

const AUTO_MODE_CHECK_INTERVAL_MS = 60 * 1000;

const ThemeContext = createContext({
	isDarkModeOn: true,
	theme: ThemeMode.Dark,
	themePreference: ThemePreference.Auto,
	setThemePreference: (() => undefined) as (preference: ThemePreference) => void,
});
const appFontFamily = '"Lexend Deca", "Helvetica", "Arial", sans-serif';
export const PAPER_BORDER: Record<'light' | 'dark', string> = {
	light: '1px solid hsla(0, 0%, 100%, 0.6)',
	dark: '1px solid hsla(230, 15%, 22%, 0.5)',
};

const getTimeOfDayThemeMode = () => {
	const hours = new Date().getHours();
	return hours >= 18 || hours < 6 ? ThemeMode.Dark : ThemeMode.Light;
};

const resolveThemeMode = (preference: ThemePreference) => {
	switch (preference) {
	case ThemePreference.Light:
		return ThemeMode.Light;
	case ThemePreference.Dark:
		return ThemeMode.Dark;
	case ThemePreference.Auto:
		return getTimeOfDayThemeMode();
	}
};

export function ThemeProvider({ children }: {children: ReactNode}) {
	const [themePreference, setThemePreferenceState] = useState(localStorageGetThemePreference);
	const [theme, setTheme] = useState(() => resolveThemeMode(themePreference));

	useEffect(() => {
		setTheme(resolveThemeMode(themePreference));
		if (themePreference !== ThemePreference.Auto) {
			return;
		}
		const interval = setInterval(() => setTheme(getTimeOfDayThemeMode()), AUTO_MODE_CHECK_INTERVAL_MS);
		return () => clearInterval(interval);
	}, [themePreference]);

	const setThemePreference = (preference: ThemePreference) => {
		localStorageSetThemePreference(preference);
		setThemePreferenceState(preference);
	};

	return (
		<ThemeContext.Provider value={{ theme, themePreference, setThemePreference, isDarkModeOn: theme === ThemeMode.Dark }}>
			<EmotionThemeProvider theme={themes[theme]}>
				{children}
			</EmotionThemeProvider>
		</ThemeContext.Provider>
	);
}

export const useThemeContext = () => useContext(ThemeContext);

const lightTheme: Theme = createTheme({
	typography: {
		fontFamily: appFontFamily,
	},
	palette: {
		mode: ThemeMode.Light,
		primary: {
			main: '#3A3042', // dark purple
		},
		background: {
			default: '#f5f5f5', // light gray
		},
	},
	components: {
		MuiBackdrop: {
			styleOverrides: {
				root: {
					'&.MuiModal-backdrop': {
						backgroundColor: 'rgba(0, 0, 0, 0.2)',
						backdropFilter: 'blur(4px)',
						WebkitBackdropFilter: 'blur(4px)',
					},
					'&.MuiBackdrop-invisible': {
						backgroundColor: 'transparent',
						backdropFilter: 'none',
						WebkitBackdropFilter: 'none',
					},
				},
			},
		},
		MuiPaper: {
			styleOverrides: {
				root: {
					// zen-list glass-card (light)
					backgroundColor: 'hsla(0, 0%, 100%, 0.45)',
					border: PAPER_BORDER.light,
					backdropFilter: 'blur(16px)',
					WebkitBackdropFilter: 'blur(16px)',
					borderRadius: 12,
					boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1), 0 8px 10px -6px rgba(0,0,0,0.1)',
					'&.MuiPaper-elevation0': {
						boxShadow: 'none',
					}
				},
			},
		},
		MuiAccordion: {
			styleOverrides: {
				root: {
					backgroundColor: 'transparent',
					boxShadow: 'none',
					backgroundImage: 'none',
					borderRadius: 0,
					'&.Mui-expanded::before': {
						opacity: 1,
					},
				},
			},
		},
		MuiButton: {
			styleOverrides: {
				root: {
					borderRadius: 24,
					padding: '8px 16px',
				},
			},
		},
		// MuiListItemButton: {
		// 	styleOverrides: {
		// 		root: {
		// 			borderRadius: 8,
		// 		},
		// 	},
		// },
	},
});

const darkTheme: Theme = createTheme({
	typography: {
		fontFamily: appFontFamily,
	},
	palette: {
		mode: ThemeMode.Dark,
		primary: {
			main: '#c2de9f',
		},
		background: {
			default: '#282828',
		}
	},
	components: {
		MuiBackdrop: {
			styleOverrides: {
				root: {
					'&.MuiModal-backdrop': {
						backgroundColor: 'rgba(0, 0, 0, 0)',
						backdropFilter: 'blur(4px)',
						WebkitBackdropFilter: 'blur(4px)',
					},
					'&.MuiBackdrop-invisible': {
						backgroundColor: 'transparent',
						backdropFilter: 'none',
						WebkitBackdropFilter: 'none',
					},
				},
			},
		},
		MuiPaper: {
			styleOverrides: {
				root: {
					// zen-list glass-card (dark)
					backgroundColor: 'rgba(40, 40, 40, 0.8)',
					border: PAPER_BORDER.dark,
					backdropFilter: 'blur(16px)',
					WebkitBackdropFilter: 'blur(16px)',
					borderRadius: 12,
					boxShadow: '0 20px 25px -5px rgba(0,0,0,0.3), 0 8px 10px -6px rgba(0,0,0,0.2)',
					'&.MuiPaper-elevation0': {
						boxShadow: 'none',
					}
				}
			}
		},
		MuiAccordion: {
			styleOverrides: {
				root: {
					backgroundColor: 'transparent',
					boxShadow: 'none',
					backgroundImage: 'none',
					borderRadius: 0,
					'&.Mui-expanded::before': {
						opacity: 1,
					},
				},
			},
		},
		MuiButton: {
			styleOverrides: {
				root: {
					borderRadius: 24,
					padding: '8px 16px',
				},
			},
		},
		// MuiListItemButton: {
		// 	styleOverrides: {
		// 		root: {
		// 			borderRadius: 8,
		// 		},
		// 	},
		// },
	},
});

export const themes: Record<ThemeMode, Theme> = {
	[ThemeMode.Light]: lightTheme,
	[ThemeMode.Dark]: darkTheme,
};
